import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useSocket } from '../contexts/SocketContext';
import { API_URL } from '../config';
import type { Question, SabotageAction, SabotageType } from '../types';

export default function Admin() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const { 
    game, 
    activeQuestion, 
    timeRemaining, 
    questionResults,
    finalResults,
    isConnected,
    joinAsAdmin, 
    startGame, 
    nextQuestion, 
    skipQuestion,
    applySabotage,
    revealMol 
  } = useSocket();

  const [gameName, setGameName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [selectedSabotage, setSelectedSabotage] = useState<SabotageType>('none');
  const [wrongHint, setWrongHint] = useState('');
  const [hiddenOptionIndex, setHiddenOptionIndex] = useState<number>(0);
  const [availableQuestions, setAvailableQuestions] = useState<Question[]>([]);

  // Load questions from API
  useEffect(() => {
    fetch(`${API_URL}/api/questions`)
      .then(res => res.json())
      .then(data => setAvailableQuestions(data))
      .catch(err => console.error('Failed to load questions:', err));
  }, []);

  // Join existing game as admin
  useEffect(() => {
    if (gameId && isConnected) {
      joinAsAdmin(gameId);
    }
  }, [gameId, isConnected]);

  const createGame = async () => {
    if (!gameName.trim()) return;
    
    setIsCreating(true);
    try {
      const response = await fetch(`${API_URL}/api/games`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: gameName,
          questions: availableQuestions
        })
      });
      
      const newGame = await response.json();
      navigate(`/admin/${newGame.id}`);
    } catch (err) {
      console.error('Failed to create game:', err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleSabotage = () => {
    if (!game || !activeQuestion || selectedSabotage === 'none') return;
    
    const sabotage: SabotageAction = {
      questionId: activeQuestion.question.id,
      type: selectedSabotage,
      details: {}
    };
    
    if (selectedSabotage === 'wrong_hint') {
      sabotage.details = { wrongHint };
    } else if (selectedSabotage === 'hide_option') {
      sabotage.details = { hiddenOptionIndex };
    }
    
    applySabotage(game.id, sabotage);
    setSelectedSabotage('none');
  };

  // No game yet - show create form
  if (!gameId) {
    return (
      <div className="container flex-center" style={{ minHeight: '100vh' }}>
        <div className="card" style={{ width: '100%', maxWidth: '400px' }}>
          <h2 className="mb-2">🎮 Nieuw Spel Maken</h2>
          
          <input
            type="text"
            placeholder="Naam van het spel"
            value={gameName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGameName(e.target.value)}
            className="mb-2"
          />
          
          <button 
            className="btn btn-primary btn-large" 
            style={{ width: '100%' }}
            onClick={createGame}
            disabled={isCreating || !gameName.trim() || availableQuestions.length === 0}
          >
            {isCreating ? 'Bezig...' : 'Start Spel'}
          </button>
          
          <div className="mt-2 text-center">
            <p className="text-muted" style={{ fontSize: '0.875rem' }}>
              {availableQuestions.length} vragen beschikbaar
            </p>
            <button 
              className="btn btn-secondary mt-1"
              style={{ fontSize: '0.875rem' }}
              onClick={() => navigate('/admin/vragen')}
            >
              📝 Vragen Beheren
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (!game) {
    return (
      <div className="container flex-center" style={{ minHeight: '100vh' }}>
        <p>Laden...</p>
      </div>
    );
  }

  const joinUrl = `${window.location.origin}/join/${game.id}`;
  const screenUrl = `${window.location.origin}/scherm/${game.id}`;

  return (
    <div className="container" style={{ paddingTop: '20px', paddingBottom: '20px' }}>
      {/* Header */}
      <div className="flex-between mb-3">
        <div>
          <h1 style={{ color: 'var(--color-primary)' }}>{game.name}</h1>
          <p className="text-muted">Code: {game.id} • Status: {game.status}</p>
        </div>
        <div className="flex gap-2">
          <a href={screenUrl} target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
            📺 Open Scherm
          </a>
        </div>
      </div>

      <div className="grid grid-2 gap-3">
        {/* Left column - Game control */}
        <div>
          {/* Lobby state */}
          {game.status === 'lobby' && (
            <div className="card mb-2">
              <h3 className="mb-2">Teams ({game.teams.length}/{game.settings.maxTeams})</h3>
              
              {game.teams.length === 0 ? (
                <p className="text-muted">Nog geen teams aangemeld</p>
              ) : (
                <div className="flex gap-1" style={{ flexDirection: 'column' }}>
                  {game.teams.map(team => (
                    <div key={team.id} className="team-item">
                      <div>
                        <div className="team-name">{team.name}</div>
                        <div className="team-players">{team.players.join(', ')}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <button 
                className="btn btn-primary btn-large mt-2"
                style={{ width: '100%' }}
                onClick={() => startGame(game.id)}
                disabled={game.teams.length === 0}
              >
                🚀 Start Spel
              </button>
            </div>
          )}

          {/* Playing state */}
          {game.status === 'playing' && (
            <div className="card mb-2">
              <h3 className="mb-2">Spelbesturing</h3>
              
              <div className="flex-between mb-2">
                <span>Vraag {game.currentQuestionIndex + 1} / {game.questions.length}</span>
                {activeQuestion && (
                  <span className={`timer ${timeRemaining <= 5 ? 'warning' : ''}`}>
                    {timeRemaining}s
                  </span>
                )}
              </div>
              
              {!activeQuestion && !questionResults && (
                <button 
                  className="btn btn-primary btn-large"
                  style={{ width: '100%' }}
                  onClick={() => nextQuestion(game.id)}
                >
                  ▶️ Volgende Vraag
                </button>
              )}
              
              {activeQuestion && (
                <div>
                  <div className="card mb-2" style={{ background: 'var(--color-secondary)' }}>
                    <p style={{ fontWeight: 600 }}>{activeQuestion.question.text}</p>
                  </div>
                  <button 
                    className="btn btn-secondary"
                    style={{ width: '100%' }}
                    onClick={() => skipQuestion(game.id)}
                  >
                    ⏭️ Skip Vraag
                  </button>
                </div>
              )}
              
              {questionResults && (
                <div>
                  <h4 className="mb-1">Resultaten</h4>
                  <p className="mb-2">Correct: {questionResults.correctAnswer}</p>
                  <div className="flex gap-1" style={{ flexDirection: 'column' }}>
                    {questionResults.teamResults.map(result => (
                      <div 
                        key={result.teamId} 
                        className="team-item"
                        style={{ 
                          borderLeft: `4px solid ${result.correct ? 'var(--color-success)' : 'var(--color-error)'}` 
                        }}
                      >
                        <span>{result.teamName}</span>
                        <span>{result.answer || 'Geen antwoord'}</span>
                      </div>
                    ))}
                  </div>
                  <button 
                    className="btn btn-primary btn-large mt-2"
                    style={{ width: '100%' }}
                    onClick={() => nextQuestion(game.id)}
                  >
                    ▶️ Volgende Vraag
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Voting state */}
          {game.status === 'voting' && (
            <div className="card mb-2">
              <h3 className="mb-2">Mol Stemming</h3>
              <p className="text-muted mb-2">Teams stemmen nu wie de Mol is...</p>
              
              <div className="flex gap-1" style={{ flexDirection: 'column' }}>
                {game.teams.map(team => (
                  <div key={team.id} className="team-item">
                    <span>{team.name}</span>
                    <span>{team.molVote ? '✅ Gestemd' : '⏳ Wacht...'}</span>
                  </div>
                ))}
              </div>
              
              <button 
                className="btn btn-accent btn-large mt-2"
                style={{ width: '100%' }}
                onClick={() => revealMol(game.id)}
              >
                🎭 Onthul de Mol!
              </button>
            </div>
          )}

          {/* Finished state */}
          {game.status === 'finished' && finalResults && (
            <div className="card mb-2">
              <h3 className="mb-2">🏆 Eindstand</h3>
              <div className="scoreboard">
                {finalResults.teams.map((team, index) => (
                  <div 
                    key={team.teamId} 
                    className={`scoreboard-item ${index === 0 ? 'winner' : ''}`}
                  >
                    <div className="scoreboard-rank">#{index + 1}</div>
                    <div className="scoreboard-team">{team.teamName}</div>
                    <div className="scoreboard-score">
                      {team.totalScore} pts
                      {team.molBonus > 0 && <span style={{ fontSize: '0.75rem' }}> (+{team.molBonus} bonus)</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right column - QR code and sabotage */}
        <div>
          {/* QR Code */}
          {game.status === 'lobby' && (
            <div className="card mb-2 text-center">
              <h3 className="mb-2">🔗 Team Join Link</h3>
              <div className="qr-container mb-2">
                <QRCodeSVG value={joinUrl} size={200} />
              </div>
              <p className="text-muted" style={{ fontSize: '0.875rem', wordBreak: 'break-all' }}>
                {joinUrl}
              </p>
            </div>
          )}

          {/* Sabotage Panel */}
          {game.status === 'playing' && activeQuestion && (
            <div className="sabotage-panel">
              <h3 className="mb-2">🦊 Sabotage (Mol Panel)</h3>
              
              <button 
                className={`sabotage-btn ${selectedSabotage === 'wrong_hint' ? 'active' : ''}`}
                onClick={() => setSelectedSabotage('wrong_hint')}
              >
                <span className="sabotage-icon">💬</span>
                <div>
                  <div style={{ fontWeight: 600 }}>Foute Hint</div>
                  <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>Geef een misleidende hint</div>
                </div>
              </button>
              
              {selectedSabotage === 'wrong_hint' && (
                <div className="mb-2">
                  <input
                    type="text"
                    placeholder="Typ je foute hint..."
                    value={wrongHint}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWrongHint(e.target.value)}
                    className="mb-1"
                  />
                </div>
              )}
              
              <button 
                className={`sabotage-btn ${selectedSabotage === 'hide_option' ? 'active' : ''}`}
                onClick={() => setSelectedSabotage('hide_option')}
              >
                <span className="sabotage-icon">👁️</span>
                <div>
                  <div style={{ fontWeight: 600 }}>Verberg Optie</div>
                  <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>Maak een antwoord onzichtbaar</div>
                </div>
              </button>
              
              {selectedSabotage === 'hide_option' && (
                <div className="mb-2">
                  <select 
                    value={hiddenOptionIndex}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setHiddenOptionIndex(Number(e.target.value))}
                  >
                    {activeQuestion.question.options.map((opt, i) => (
                      <option key={i} value={i}>Verberg: {opt}</option>
                    ))}
                  </select>
                </div>
              )}
              
              <button 
                className={`sabotage-btn ${selectedSabotage === 'halve_time' ? 'active' : ''}`}
                onClick={() => setSelectedSabotage('halve_time')}
              >
                <span className="sabotage-icon">⏱️</span>
                <div>
                  <div style={{ fontWeight: 600 }}>Halveer Tijd</div>
                  <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>Halveer de resterende tijd</div>
                </div>
              </button>
              
              {selectedSabotage !== 'none' && (
                <button 
                  className="btn btn-primary mt-2"
                  style={{ width: '100%' }}
                  onClick={handleSabotage}
                  disabled={selectedSabotage === 'wrong_hint' && !wrongHint.trim()}
                >
                  ⚡ Voer Sabotage Uit
                </button>
              )}
              
              {activeQuestion.sabotage && (
                <p className="text-muted mt-2 text-center">
                  ✅ Sabotage actief: {activeQuestion.sabotage.type}
                </p>
              )}
            </div>
          )}

          {/* Scores */}
          {game.status !== 'lobby' && (
            <div className="card mt-2">
              <h3 className="mb-2">📊 Scores</h3>
              <div className="flex gap-1" style={{ flexDirection: 'column' }}>
                {[...game.teams]
                  .sort((a, b) => b.score - a.score)
                  .map((team, index) => (
                    <div key={team.id} className="team-item">
                      <span>#{index + 1} {team.name}</span>
                      <span className="score-badge">{team.score}</span>
                    </div>
                  ))
                }
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
